// ─────────────────────────────────────────────────────────────────────────────
// ListIgnoreCodesComponent
// ─────────────────────────────────────────────────────────────────────────────
// Paginated ignored-codes table with search, filter, sort, checkbox selection,
// bulk delete, and XLSX export.
// Responsibilities:
//   - Dispatch LoadIgnoreCodes and display the paginated result
//   - Debounced keyword search (code + name)
//   - Customer and date-range filter dialog
//   - Server-side column sorting (default: create_date desc)
//   - Checkbox selection for bulk delete
//   - XLSX export with current filters/sort applied
// No create, update, or import -- entries originate from matching dialog.
// ─────────────────────────────────────────────────────────────────────────────

import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { EMPTY, Observable, Subject } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { AppState } from 'src/app/core/app.state';
import { DEFAULT_PAGE_SIZE } from 'src/app/config/global.config';
import { IPageCount, IPagination } from 'src/app/shared/models/page.model';
import { SharedModule } from 'src/app/shared/shared.module';
import { SelectionController } from 'src/app/shared/utils/selection-controller';
import { UiModule } from 'src/app/ui/ui.module';
import { ConfirmationPopUpComponent } from 'src/app/shared/confirmation-pop-up/confirmation-pop-up.component';
import { CommonService } from 'src/app/shared/common.service';
import { CustomersService } from 'src/app/features/customers/services/customers.services';
import { FilterDialogComponent } from 'src/app/shared/components/filter-dialog/filter-dialog.component';
import { FilterDialogData, FilterFieldOption } from 'src/app/shared/components/filter-dialog/filter-dialog.models';

import { IgnoreCodeFilters, IIgnoreCode } from '../../models/ignore-code.models';
import { IgnoreCodeService } from '../../services/ignore-code.service';
import { IgnoreCodeModalComponent } from '../ignore-code-modal/ignore-code-modal.component';
import { selectIgnoreCodeItems, selectIgnoreCodeLoading, selectIgnoreCodeMetadata } from '../../store/ignore-codes.selectors';
import {
  BulkDeleteIgnoreCodes,
  BulkDeleteIgnoreCodesSuccess,
  CreateIgnoreCodeSuccess,
  LoadIgnoreCodes,
  SetIgnoreCodeFilters,
  SetIgnoreCodeSearch,
  UpdateIgnoreCodeSuccess,
} from '../../store/ignore-codes.actions';

type SortableField = 'code' | 'extracted_name' | 'customer_name' | 'source' | 'create_date';

@Component({
  selector: 'app-list-ignore-codes',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule, TranslateModule, SharedModule, UiModule],
  templateUrl: './list-ignore-codes.component.html',
  styleUrls: ['./list-ignore-codes.component.scss'],
})
export class ListIgnoreCodesComponent implements OnInit, OnDestroy {
  // ── Store selectors ───────────────────────────────────────────────────────

  ignoreCodes$: Observable<IIgnoreCode[]> = this.store.select(selectIgnoreCodeItems);
  metadata$: Observable<IPagination | null> = this.store.select(selectIgnoreCodeMetadata);
  loading$: Observable<boolean> = this.store.select(selectIgnoreCodeLoading);

  // ── Local state ───────────────────────────────────────────────────────────

  page: IPageCount = { number: 1, size: DEFAULT_PAGE_SIZE };
  keyword = '';
  filters: IgnoreCodeFilters = { customer_id: null, create_date_from: null, create_date_to: null };

  /** Row selection, range gestures and the tri-state header, in one object. */
  readonly selection = new SelectionController<IIgnoreCode>((row) => row.id);
  sortField: SortableField = 'create_date';
  sortMode: 'asc' | 'desc' = 'desc';
  sortedIgnoreCodes: IIgnoreCode[] = [];
  searchControl = new FormControl('');

  // ── Cleanup ───────────────────────────────────────────────────────────────

  private destroy$ = new Subject<void>();

  constructor(
    private store: Store<AppState>,
    private dialog: MatDialog,
    private actions$: Actions,
    private translateService: TranslateService,
    private ignoreCodeService: IgnoreCodeService,
    private customersService: CustomersService,
    private commonService: CommonService,
  ) {}

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  ngOnInit(): void {
    // Sync ignore codes from store to sortedIgnoreCodes
    this.ignoreCodes$.pipe(takeUntil(this.destroy$)).subscribe((codes) => {
      this.sortedIgnoreCodes = [...codes];
      this.selection.setRows(this.sortedIgnoreCodes);
    });

    // Debounced search input
    this.searchControl.valueChanges.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$)).subscribe((value) => {
      this.onSearch(value ?? '');
    });

    // Reload only after bulk delete -- server removed rows
    this.actions$.pipe(ofType(BulkDeleteIgnoreCodesSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.selection.clear();
      this.loadIgnoreCodes();
    });

    // Reducer optimistically updates the list on create/update -- only clear selection
    this.actions$.pipe(ofType(CreateIgnoreCodeSuccess, UpdateIgnoreCodeSuccess), takeUntil(this.destroy$)).subscribe(() => {
      this.selection.clear();
    });

    // Initial load (default: create_date desc)
    this.loadIgnoreCodes();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ── Data loading ──────────────────────────────────────────────────────────

  loadIgnoreCodes(): void {
    this.store.dispatch(
      LoadIgnoreCodes({
        page: this.page,
        key: this.keyword || undefined,
        filters: this.filters,
        sortBy: this.sortField || undefined,
        sortMode: this.sortField ? this.sortMode : undefined,
      }),
    );
  }

  toPage(page: IPageCount): void {
    this.page = { ...this.page, number: page.number, size: page.size };
    this.selection.clear();
    this.loadIgnoreCodes();
  }

  // ── Search ────────────────────────────────────────────────────────────────

  onSearch(value: string): void {
    this.keyword = value;
    this.selection.clear();
    this.store.dispatch(SetIgnoreCodeSearch({ keyword: value }));
    this.page = { ...this.page, number: 1 };
    this.loadIgnoreCodes();
  }

  // ── Filters ───────────────────────────────────────────────────────────────

  activeFilterValues: Record<string, any> = {};

  private get ignoreCodeFilterConfig(): FilterDialogData {
    return {
      config: {
        title: 'knowledge_base.ignore_codes.filter.title',
        sections: [
          {
            title: 'knowledge_base.ignore_codes.filter.group_title',
            fields: [
              {
                key: 'customer',
                label: 'filter.customer',
                type: 'autocomplete',
                placeholder: 'filter.customer_placeholder',
                searchFn: (term: string): Observable<FilterFieldOption[]> =>
                  this.customersService
                    .listCustomers({ number: 1, size: 20 }, undefined, term)
                    .pipe(map((res) => (res.data ?? []).map((c) => ({ value: c.id, label: c.name })))),
              },
              {
                key: 'create_date',
                label: 'filter.created_date',
                type: 'date-range',
                minKey: 'create_date_from',
                maxKey: 'create_date_to',
                minPlaceholder: 'common.from',
                maxPlaceholder: 'common.to',
              },
            ],
          },
        ],
      },
      values: {},
    };
  }

  setFilter(): void {
    const dialogRef = this.dialog.open(FilterDialogComponent, {
      data: {
        ...this.ignoreCodeFilterConfig,
        values: { ...this.activeFilterValues },
      } as FilterDialogData,
      width: '900px',
    });

    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: Record<string, any> | undefined) => {
        if (result) {
          this.activeFilterValues = result;
          this.filters = {
            customer_id: result['customer']?.value ?? null,
            create_date_from: result['create_date_from'] || null,
            create_date_to: result['create_date_to'] || null,
          };
          this.store.dispatch(SetIgnoreCodeFilters({ filters: this.filters }));
          this.page = { ...this.page, number: 1 };
          this.loadIgnoreCodes();
        }
      });
  }

  hasActiveFilters(): boolean {
    return !!(this.filters.customer_id || this.filters.create_date_from || this.filters.create_date_to);
  }

  clearFilter(): void {
    this.activeFilterValues = {};
    this.filters = { customer_id: null, create_date_from: null, create_date_to: null };
    this.store.dispatch(SetIgnoreCodeFilters({ filters: this.filters }));
    this.page = { ...this.page, number: 1 };
    this.loadIgnoreCodes();
  }

  // ── Sorting (server-side) ─────────────────────────────────────────────────

  onSort(field: SortableField): void {
    if (this.sortField === field) {
      this.sortMode = this.sortMode === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortField = field;
      this.sortMode = 'asc';
    }
    this.page = { ...this.page, number: 1 };
    this.loadIgnoreCodes();
  }

  // ── Checkbox selection ────────────────────────────────────────────────────



  // ── Delete ────────────────────────────────────────────────────────────────

  bulkDelete(): void {
    const dialogRef = this.dialog.open(ConfirmationPopUpComponent, {
      data: {
        icon: '../../../assets/icones/cancel_alert.svg',
        confirm_text: this.translateService.instant('common.remove'),
      },
    });
    dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result: boolean) => {
        if (result) {
          this.store.dispatch(BulkDeleteIgnoreCodes({ ids: this.selection.ids }));
        }
      });
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  exportList(): void {
    this.ignoreCodeService
      .exportIgnoreCodes('xlsx', this.keyword || undefined, this.filters, this.sortField || undefined, this.sortField ? this.sortMode : undefined)
      .pipe(
        catchError(() => {
          this.commonService.showToast(this.translateService.instant('knowledge_base.ignore_codes.toast.export_error'), 'ERROR');
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe((blob) => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `ignored_codes_${new Date().toISOString().split('T')[0]}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
      });
  }

  // ── Dialogs ───────────────────────────────────────────────────────────────

  openCreateModal(): void {
    this.dialog.open(IgnoreCodeModalComponent, {
      data: null,
      disableClose: true,
    });
  }

  openEditModal(row: IIgnoreCode): void {
    if (!this.canEdit(row)) return;
    this.dialog.open(IgnoreCodeModalComponent, {
      data: row,
      disableClose: true,
    });
  }

  canEdit(row: IIgnoreCode): boolean {
    return row.source === 'manual';
  }
}
